import { faCheck, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';

import { Color } from '@/constants/color';
import {
  useCeleryOverview,
  usePurgeQueueByTaskPath,
  useQueueBreakdown
} from '@/hooks/useCeleryConsole';
import Button from '@/ui/components/Button';
import Tooltip from '@/ui/components/Tooltip';

import { ErrorDetail, Loading, PanelCard, PanelError } from '../../../shared';
import { PollInterval } from '../../../types';
import QueueBadge from '../components/QueueBadge';

const BreakdownDetail = ({ queue }: { queue: string }) => {
  const [full, setFull] = useState(false);
  // Fires on expand or when ``full`` toggles — see useQueueBreakdown (no
  // refetchInterval). Full mode is opt-in: deep queues stay sampled by
  // default.
  const { data, isLoading, error } = useQueueBreakdown(queue, undefined, full);
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorDetail error={error} />;
  if (data.breakdown.length === 0) {
    return <p className="text-sm text-gray-500">Queue is empty.</p>;
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-500">
          {data.mode === 'full'
            ? `Full pass of ${data.sampled.toLocaleString()} entries.`
            : `Sampled ${data.head_count.toLocaleString()} from head + ${data.tail_count.toLocaleString()} from tail of ${data.depth.toLocaleString()} entries.`}{' '}
          Percent is of the sample.
        </p>
        {data.mode === 'head_tail' && (
          <Button color={Color.GRAY} onClick={() => setFull(true)} size="xs">
            Run full pass
          </Button>
        )}
        {full && data.mode === 'full' && (
          <Button color={Color.GRAY} onClick={() => setFull(false)} size="xs">
            Back to sample
          </Button>
        )}
      </div>
      <BreakdownRows queue={queue} entries={data.breakdown} />
    </div>
  );
};

const BreakdownRows = ({
  queue,
  entries
}: {
  queue: string;
  entries: { task: string; count: number; percent: number }[];
}) => {
  const purge = usePurgeQueueByTaskPath();
  const queryClient = useQueryClient();
  // Per-row visual state — single mutation, but the spinner / check needs to
  // land on the row the engineer actually clicked, not every red button.
  const [purgingTaskPath, setPurgingTaskPath] = useState<string | null>(null);
  const [succeededTaskPath, setSucceededTaskPath] = useState<string | null>(null);
  const SUCCESS_FEEDBACK_MS = 2000;

  const onPurge = (taskPath: string, count: number) => {
    const confirmed = window.confirm(
      `Purge every "${taskPath}" entry from "${queue}"?\n\n` +
        `The breakdown sample saw ${count.toLocaleString()} of these. The actual ` +
        `number removed depends on the live queue depth at purge time. This is a ` +
        `bulk LREM — entries are discarded, not re-routed, and the action cannot ` +
        `be undone.`
    );
    if (!confirmed) return;
    setPurgingTaskPath(taskPath);
    purge.mutate(
      { queue, taskPath },
      {
        onSuccess: () => {
          setPurgingTaskPath(null);
          setSucceededTaskPath(taskPath);
          // Hold the green check for the feedback window before invalidating —
          // otherwise the breakdown refetches, the row disappears, and the
          // engineer never sees the "done" state. The fetched depth on the
          // overview tile is mildly stale during this window, which is fine.
          setTimeout(() => {
            setSucceededTaskPath(null);
            queryClient.invalidateQueries({ queryKey: ['celeryOverview'] });
            queryClient.invalidateQueries({ queryKey: ['celeryQueueBreakdown'] });
          }, SUCCESS_FEEDBACK_MS);
        },
        onError: () => setPurgingTaskPath(null)
      }
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="py-2">Task</th>
            <th className="py-2 px-3 text-right whitespace-nowrap">% of sample</th>
            <th className="py-2 px-3 text-right whitespace-nowrap">Count</th>
            <th className="py-2 pl-3" />
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => {
            const isPurging = purgingTaskPath === entry.task;
            const justSucceeded = succeededTaskPath === entry.task;
            const icon = isPurging ? faSpinner : justSucceeded ? faCheck : faTrash;
            const color = justSucceeded ? Color.GREEN : Color.RED;
            return (
              <tr key={entry.task} className="border-t">
                <td className="py-2 font-mono">{entry.task}</td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  {entry.percent.toFixed(2)}%
                </td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  {entry.count.toLocaleString()}
                </td>
                <td className="py-2 pl-3 text-right whitespace-nowrap">
                  <Tooltip
                    tooltipContent={
                      isPurging
                        ? `Purging "${entry.task}" from "${queue}"…`
                        : justSucceeded
                          ? `Purged "${entry.task}" from "${queue}".`
                          : `Bulk LREM every "${entry.task}" entry from "${queue}" — these tasks are discarded and will not run.`
                    }>
                    <Button
                      color={color}
                      disabled={purge.isPending || justSucceeded}
                      onClick={() => onPurge(entry.task, entry.count)}>
                      <FontAwesomeIcon icon={icon} spin={isPurging} />
                    </Button>
                  </Tooltip>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const QueuesPanel = ({ interval }: { interval: PollInterval }) => {
  const { data, isLoading, error } = useCeleryOverview(interval);
  const [expanded, setExpanded] = useState<string | null>(null);

  if (isLoading && !data) return <Loading />;
  if (error) return <PanelError title="Queues" error={error} />;
  const queues = data?.queues ?? {};
  const toggle = (name: string) => setExpanded((prior) => (prior === name ? null : name));

  return (
    <PanelCard title={`Queues — ${data?.unacked_count ?? 0} unacked`}>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-2 w-4" />
              <th className="py-2">Queue</th>
              <th className="py-2 text-right">Depth</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(queues).map(([name, depth]) => {
              const isOpen = expanded === name;
              return (
                <Fragment key={name}>
                  <tr
                    className="border-t cursor-pointer hover:bg-gray-50"
                    onClick={() => toggle(name)}>
                    <td className="py-2 text-xs text-gray-400">{isOpen ? '▼' : '▶'}</td>
                    <td className="py-2">
                      <QueueBadge queue={name} />
                    </td>
                    <td className="py-2 text-right">{depth.toLocaleString()}</td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-gray-50">
                      <td colSpan={3} className="px-3 py-2">
                        <BreakdownDetail queue={name} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </PanelCard>
  );
};

export default QueuesPanel;
